/**
 * Theme, density and language.
 *
 * All three are set on the document element, exactly as the design tokens expect. That is
 * not a stylistic preference: a theme change becomes one attribute swap and zero React
 * re-renders, and every component picks it up through CSS custom properties it was already
 * reading.
 *
 * These *are* persisted, unlike the session. Someone who chose dark mode and Arabic has said
 * something about how they want to work, and asking again every morning is the kind of small
 * disrespect that makes a tool feel like a burden.
 */
import { create } from 'zustand'

import i18n, { applyLanguage, type Language } from '@/i18n'

export type Theme = 'light' | 'dark'
export type Density = 'comfortable' | 'compact'

const KEYS = {
  theme: 'epmo.theme',
  density: 'epmo.density',
  language: 'epmo.language',
  rail: 'epmo.rail',
  registers: 'epmo.registers',
} as const

function stored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const value = localStorage.getItem(key)
    return allowed.includes(value as T) ? (value as T) : fallback
  } catch {
    // Private browsing, or storage disabled by policy. A preference is not worth failing
    // the application over.
    return fallback
  }
}

function remember(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* see above */
  }
}

type PreferenceState = {
  theme: Theme
  density: Density
  language: Language
  railOpen: boolean
  registersOpen: boolean
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
  setDensity: (density: Density) => void
  toggleDensity: () => void
  setLanguage: (language: Language) => void
  toggleRail: () => void
  toggleRegisters: () => void
}

export const usePreferences = create<PreferenceState>((set, get) => ({
  theme: stored<Theme>(KEYS.theme, ['light', 'dark'], 'light'),
  density: stored<Density>(KEYS.density, ['comfortable', 'compact'], 'comfortable'),
  language: stored<Language>(KEYS.language, ['en', 'ar'], 'en'),

  // The rail collapses to its icon circles, which is what gives a 29-column register grid
  // the width it needs. Remembered, because someone who collapsed it did so for a reason
  // that will still hold tomorrow.
  railOpen: stored(KEYS.rail, ['open', 'closed'], 'open') === 'open',
  registersOpen: stored(KEYS.registers, ['open', 'closed'], 'open') === 'open',

  setTheme(theme) {
    document.documentElement.dataset['theme'] = theme
    remember(KEYS.theme, theme)
    set({ theme })
  },

  toggleTheme() {
    get().setTheme(get().theme === 'light' ? 'dark' : 'light')
  },

  setDensity(density) {
    document.documentElement.dataset['density'] = density
    remember(KEYS.density, density)
    set({ density })
  },

  toggleDensity() {
    get().setDensity(get().density === 'comfortable' ? 'compact' : 'comfortable')
  },

  toggleRail() {
    const railOpen = !get().railOpen
    remember(KEYS.rail, railOpen ? 'open' : 'closed')
    set({ railOpen })
  },

  toggleRegisters() {
    const registersOpen = !get().registersOpen
    remember(KEYS.registers, registersOpen ? 'open' : 'closed')
    set({ registersOpen })
  },

  setLanguage(language) {
    void i18n.changeLanguage(language)
    applyLanguage(language)
    remember(KEYS.language, language)
    set({ language })
  },
}))

/** Apply stored preferences to the document. Called once, before first paint. */
export function hydratePreferences(): void {
  const { theme, density, language } = usePreferences.getState()
  document.documentElement.dataset['theme'] = theme
  document.documentElement.dataset['density'] = density
  void i18n.changeLanguage(language)
  applyLanguage(language)
}
